import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  AlertTriangle,
  Ban,
  CheckCircle2,
  CloudUpload,
  Copy,
  Eye,
  FileEdit,
  FileText,
  History,
  Images,
  Layers,
  Link2,
  ListChecks,
  Loader2,
  RotateCcw,
  Trash2,
  Wrench,
} from 'lucide-react'
import { hasPermission } from '../../../app/access'
import { Badge } from '../../../components/ui/Badge'
import { Button } from '../../../components/ui/Button'
import { Modal } from '../../../components/ui/Modal'
import { EntityChain, type ChainItem } from '../../../components/ui/EntityChain'
import { copyToClipboard } from '../../../components/ui/copyToClipboard'
import type { IdMetaItem } from '../../../components/ui/IdMeta'
import {
  NextStepBar,
  type NextStepAction,
  type NextStepCheck,
  type NextStepTone,
} from '../../../components/ui/NextStepBar'
import type { EntityMoreMenuItem } from '../../../components/entity-view/EntityModalShell'
import { useEntityNavigator } from '../../../components/entity-view/navigatorContext'
import { buildPresenceChain, useProductPresence } from '../../products-v2/presence'
import { isNoDesignCode } from '../../products-v2/lib'
import { isPublishableCard } from '../../../components/marketplace-publish'
import { getMediaUrl } from '../../../utils/mediaUtils'
import { AuthorizedMediaImage } from '../../../shared/media/authorizedMedia'
import { useAuthStore } from '../../../stores/authStore'
import { useToastStore } from '../../../stores/toastStore'
import { CatalogCardModal } from './CatalogCardModal'
import { UnderlineTabs } from '../../../components/ui/UnderlineTabs'
import { CardSidebar } from './CardSidebar'
import { UnsavedChangesDialog } from '../../seo-v2/components/UnsavedChangesDialog'
import { refusalHint } from '../../designs-v2/components/design-card/marketplaceReasons'
import { AuthorizedLightbox } from './AuthorizedLightbox'
import { MediaRibbon } from './media-ribbon/MediaRibbon'
import {
  CharacteristicValueInput,
  type CharacteristicValueItem,
} from '../../marketplace-characteristics/CharacteristicValueInput'
import { categoryMpCharacteristicsAPI } from '../../categories/mpCharacteristics.api'
import { marketplaceMappingAPI } from '../../marketplace-mapping/api'
import {
  marketplaceReferenceAPI,
  type OzonCategoryAttribute,
  type WbSubjectCharacteristic,
} from '../../marketplace-reference/api'
import { yandexMarketAPI, type YmCategoryParameter } from '../../yandex-market/api'
import { assertNever, marketplaceProfile } from '../../../shared/marketplaces'
import { draftStateInfo, formatDraftDateTime, publicationModeLabel } from '../draftColumns'
import { wbErrorBadge } from '../wbDraftState'
import {
  pimMarketplaceCardsAPI,
  type PimMarketplace,
  type PimMarketplaceCard,
  type PimMarketplacePayloadPreview,
} from '../pim-drafts-api'

interface PimDraftCardModalProps {
  card: PimMarketplaceCard | null
  cards: PimMarketplaceCard[]
  onNavigate: (id: number) => void
  onClose: () => void
  onPublish?: (card: PimMarketplaceCard) => void
  /**
   * Исключить карточку из выгрузки или вернуть её. Запрос и обновление списка
   * живут у владельца данных (страница черновиков или навигатор), окно только просит.
   */
  onSetExclusion?: (
    card: PimMarketplaceCard,
    excluded: boolean,
    reason?: string | null,
  ) => Promise<void> | void
  /** Удалить черновик: подтверждение и запрос — у владельца данных. */
  onDelete?: (card: PimMarketplaceCard) => void
  /** Окно открыто навигатором по цепочке: есть куда вернуться (кнопка и Alt+←). */
  canGoBack?: boolean
  onBack?: () => void
  /** Сколько всего карточек в выборке, если загружена только часть. */
  totalCount?: number
}

type PimDraftTab = 'overview' | 'text' | 'characteristics' | 'media' | 'merge' | 'history'

const NEED_UPDATE_RIGHT = 'Нужно право Card:UPDATE'
/** Охват правки фото черновика: только эта карточка (overrides.media_urls). */
const RIBBON_SCOPES = [{ key: 'card', label: 'Эта карточка' }]
const NEED_DELETE_RIGHT = 'Нужно право Card:DELETE'

type EditableFillLevel = 'category' | 'product'

interface EditableCharacteristic {
  targetId: number
  name: string
  level: EditableFillLevel
  required: boolean
  multiple: boolean
  maxCount: number | null
  number: boolean
  dictionaryCode: string | null
  sortOrder: number
  /** Локальное поле PIM существует только в levels, не в справочнике WB. */
  localOnly: boolean
}

const OZON_MEDIA_CHARACTERISTIC_IDS = new Set([11254, 21837, 21841, 21845, 22273])
const WB_MEDIA_CHARACTERISTIC_IDS = new Set([-1000, -1001])
const LONG_TEXT_CHARACTERISTIC_IDS = new Set([
  11254, 21837, 21841, 21845, 22273, 23171, -1000, -1001,
])

/** Площадка отказала или прошлая выгрузка завершилась ошибкой: кнопка называется «Повторить». */
const isRefusedCard = (card: Pick<PimMarketplaceCard, 'status' | 'submit_state'>): boolean => (
  String(card.status ?? '').trim().toLowerCase() === 'error'
  || String(card.submit_state ?? '').trim().toLowerCase() === 'rejected'
)

const truthyFlag = (value: unknown): boolean => (
  value === true || value === 1 || value === '1'
)

const normalizedLimit = (value: unknown): number | null => {
  const parsed = Number(value)
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null
}

const rawCharacteristicItems = (
  raw: unknown,
  multiple: boolean,
): CharacteristicValueItem[] => {
  if (raw === null || raw === undefined) return []
  const container = raw && typeof raw === 'object' && !Array.isArray(raw)
    ? ((raw as any).values ?? (raw as any).value ?? raw)
    : raw
  const source = Array.isArray(container) ? container : [container]
  const result: CharacteristicValueItem[] = []
  for (const entry of source) {
    // Я.Маркет называет id значения `valueId` (camelCase) — WB/OZON этого поля
    // не используют, так что фолбэк для них ничего не меняет.
    const value = entry && typeof entry === 'object'
      ? String((entry as any).value ?? (entry as any).dictionary_value_id ?? (entry as any).valueId ?? '').trim()
      : String(entry ?? '').trim()
    const valueId = entry && typeof entry === 'object'
      ? ((entry as any).dictionary_value_id ?? (entry as any).value_id ?? (entry as any).valueId ?? null)
      : null
    // Категорийная форма хранит коллекции через `;`. Запятая является частью
    // допустимых словарных значений OZON (например, у «Упаковки») и не может
    // считаться разделителем.
    const parts = multiple && !Array.isArray(container)
      ? value.split(';').map((part) => part.trim()).filter(Boolean)
      : [value]
    for (const part of parts) {
      if (part) result.push({ value: part, valueId: parts.length === 1 ? valueId : null })
    }
  }
  return result
}

const characteristicRawValue = (
  values: CharacteristicValueItem[],
  multiple: boolean,
): unknown => multiple
  ? values.map((item) => item.value.trim()).filter(Boolean)
  : (values[0]?.value ?? '')

const applicableDefinitions = (
  marketplace: 'wb' | 'ozon',
  reference: Array<WbSubjectCharacteristic | OzonCategoryAttribute>,
  levels: Array<{ target_id: number; target_name: string; fill_level: string }>,
): EditableCharacteristic[] => {
  const levelByTarget = new Map(
    levels.map((row) => [Number(row.target_id), String(row.fill_level)]),
  )
  const definitions: EditableCharacteristic[] = reference
    .filter((row: any) => truthyFlag(row.is_active))
    .map((row: any, index) => {
      const targetId = Number(marketplace === 'wb' ? row.charc_id : row.attribute_id)
      const level = levelByTarget.get(targetId) ?? 'category'
      if (level !== 'category' && level !== 'product') return null
      return {
        targetId,
        name: String(row.name || targetId),
        level,
        required: truthyFlag(row.is_required),
        multiple: marketplace === 'wb'
          ? truthyFlag(row.is_list) || Number(row.max_count) > 1
          : truthyFlag(row.is_collection),
        maxCount: normalizedLimit(
          marketplace === 'wb' ? row.max_count : row.max_value_count,
        ),
        number: marketplace === 'wb'
          ? row.value_type === 'number'
          : /integer|decimal|float|number/i.test(String(row.type || '')),
        dictionaryCode: marketplace === 'wb'
          ? (row.dictionary_code ? String(row.dictionary_code) : null)
          : (row.dictionary_id && String(row.dictionary_id) !== '0'
              ? String(row.dictionary_id)
              : null),
        sortOrder: index * 10,
        localOnly: false,
      } as EditableCharacteristic
    })
    .filter((row): row is EditableCharacteristic => row !== null && Number.isFinite(row.targetId))

  if (marketplace === 'wb') {
    for (const row of levels) {
      const targetId = Number(row.target_id)
      if (targetId > -1000 || (row.fill_level !== 'category' && row.fill_level !== 'product')) continue
      definitions.push({
        targetId,
        name: String(row.target_name || targetId),
        level: row.fill_level,
        required: false,
        multiple: false,
        maxCount: null,
        number: false,
        dictionaryCode: null,
        sortOrder: 1_000_000 + Math.abs(targetId),
        localOnly: true,
      })
    }
  }

  return definitions.sort((left, right) => (
    left.level.localeCompare(right.level)
    || left.sortOrder - right.sortOrder
    || left.targetId - right.targetId
  ))
}

/**
 * Характеристики Я.Маркет — своя форма справочника (`ym_category_parameters`,
 * `features/yandex-market/api.ts#getCategoryParameters`), несовместимая с
 * WB/OZON (`parameter_id`/`multivalue`/`type` вместо `charc_id`/`attribute_id`/
 * `is_collection`). Сведение шло бы в `applicableDefinitions` тернарником
 * «wb или всё остальное» — ровно та ошибка, ради которой заведён реестр
 * площадок (SPEC_2026-09-13_YANDEX_MARKET.md §3.1), поэтому отдельная функция.
 *
 * У Маркета нет разницы «категорийная / товарная» характеристика — все поля
 * уровня оффера, поэтому уровень фиксирован как `product`. Значения ENUM
 * приходят от площадки целиком (не постранично, как словари WB/OZON) — они
 * подставляются напрямую в `dictionaryOptions`, без обращения к `loadDictionary`.
 */
const applicableYmDefinitions = (parameters: YmCategoryParameter[]): EditableCharacteristic[] =>
  parameters
    .map((row, index): EditableCharacteristic | null => {
      const targetId = Number(row.parameter_id)
      if (!Number.isFinite(targetId)) return null
      return {
        targetId,
        name: String(row.name || targetId),
        level: 'product',
        required: Boolean(row.required),
        multiple: Boolean(row.multivalue),
        maxCount: null,
        number: /^(numeric|number|int|decimal|float)/i.test(String(row.type || '')),
        dictionaryCode: Array.isArray(row.values) && row.values.length > 0 ? `ym:${targetId}` : null,
        sortOrder: index * 10,
        localOnly: false,
      }
    })
    .filter((row): row is EditableCharacteristic => row !== null)
    .sort((left, right) => left.sortOrder - right.sortOrder)

/**
 * Строка «поле → значение».
 *
 * Подпись человеческая, технический ключ площадки — мелким под ней. Окно
 * показывает фактическое тело отправки, поэтому ключ прятать нельзя: по нему
 * сверяют с документацией площадки и с логом отказа. Но и оставлять только
 * его нельзя — оператор читает карточку, а не схему API.
 */
const Kv = ({
  label,
  apiKey,
  children,
}: {
  label: string
  apiKey?: string
  children: React.ReactNode
}) => (
  <div className="grid grid-cols-[13rem_minmax(0,1fr)] gap-3 border-b border-gray-100 py-2.5 text-sm last:border-b-0 dark:border-zinc-800">
    <dt className="min-w-0">
      <span className="text-gray-700 dark:text-zinc-300">{label}</span>
      {apiKey && (
        <span className="mt-0.5 block truncate font-mono text-[10px] text-gray-400 dark:text-zinc-500">
          {apiKey}
        </span>
      )}
    </dt>
    <dd className="min-w-0 break-words text-gray-900 dark:text-zinc-100">{children}</dd>
  </div>
)

const textValue = (value: unknown) => {
  if (value === null || value === undefined || value === '') return '—'
  if (typeof value === 'object') return JSON.stringify(value, null, 2)
  return String(value)
}

/** «349 ₽» с неразрывным пробелом — как принято в дизайн-системе. */
const money = (value: unknown): string => {
  const number = Number(value)
  return Number.isFinite(number) ? `${number.toLocaleString('ru-RU')} ₽` : '—'
}

/**
 * Поле правки. Изменённое руками подсвечивается янтарным — так на экране
 * сразу видно, что уедет не сгенерированное значение.
 */
const editField = (overridden: boolean): string =>
  `w-full rounded-md border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-zinc-950 dark:text-zinc-100 ${
    overridden
      ? 'border-amber-400 bg-amber-50/60 dark:border-amber-700 dark:bg-amber-950/20'
      : 'border-gray-300 bg-white dark:border-zinc-700'
  }`

const ManualMark: React.FC = () => (
  <span className="mt-1 block text-[10px] text-amber-700 dark:text-amber-400">
    изменено вручную
  </span>
)

/**
 * Габариты и вес брутто одной строкой.
 *
 * Единицы сознательно одни для обеих площадок — сантиметры и граммы, как на
 * упаковке. У ВБ в теле сантиметры и килограммы, у Ozon миллиметры и граммы;
 * перевод делают показ и сервис, а человек не должен помнить, чья это карточка.
 */
const DIMENSION_FIELDS: Array<{ key: 'length' | 'width' | 'height' | 'weightBrutto'; label: string; unit: string }> = [
  { key: 'length', label: 'Длина', unit: 'см' },
  { key: 'width', label: 'Ширина', unit: 'см' },
  { key: 'height', label: 'Высота', unit: 'см' },
  { key: 'weightBrutto', label: 'Вес брутто', unit: 'г' },
]

/**
 * Пояснения к характеристикам, которые путаются с габаритами упаковки.
 *
 * У ВБ размеры живут в двух местах: `dimensions` в корне карточки — это
 * упаковка и логистика, а «Высота предмета» и «Вес товара без упаковки» —
 * обычные характеристики, описание товара для покупателя. На экране обе группы
 * выглядели одинаково, и разницу приходилось объяснять словами.
 *
 * Ключ — идентификатор, а не имя: имена приходят из справочника площадки и
 * могут быть переформулированы, идентификатор же и есть контракт.
 */
const CHARACTERISTIC_HINTS: Record<string, string> = {
  '90630': 'размер самого товара, не упаковки',
  '90673': 'размер самого товара, не упаковки',
  '90652': 'размер самого товара, не упаковки',
  '89008': 'вес без упаковки; вес брутто — во вкладке «Текст»',
}

const DimensionFields: React.FC<{
  form: Record<string, string>
  setForm: (next: any) => void
  overridden: boolean
}> = ({ form, setForm, overridden }) => (
  <div>
    <p className="mb-2 text-[11px] text-gray-500 dark:text-zinc-400">
      Размеры коробки или конверта — по ним площадка считает логистику и тариф.
      Размеры самого товара живут отдельно, во вкладке «Характеристики».
    </p>
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
      {/*
        Подпись уточняет, что это упаковка: рядом, в характеристиках, есть
        «Высота предмета» и «Вес товара без упаковки» — другие поля с другим
        смыслом. Без уточнения два блока про размеры выглядят дублем.
      */}
      {DIMENSION_FIELDS.map(({ key, label, unit }) => (
        <label key={key} className="min-w-0">
          <span className="mb-1 block text-[11px] text-gray-500 dark:text-zinc-400">
            {label}, {unit}
          </span>
          <input
            type="number"
            min="0"
            step="0.1"
            value={form[key] ?? ''}
            onChange={(event) => setForm({ ...form, [key]: event.target.value })}
            className={editField(overridden)}
          />
        </label>
      ))}
    </div>
    {overridden && <ManualMark />}
  </div>
)

const responseError = (error: any) => String(
  error?.response?.data?.error?.message
    || error?.response?.data?.message
    || error?.message
    || 'Не удалось собрать payload карточки',
)

/** Короткое имя площадки для метки «Черновик …». */
const KIND_MARKETPLACE: Record<PimMarketplace, string> = {
  wb: 'ВБ',
  ozon: 'ОЗОН',
  ym: 'Маркета',
}

const NO_DATA = 'нет данных'

const NO_PERMISSIONS: string[] = []

const pluralFields = (count: number): string => {
  const rest10 = count % 10
  const rest100 = count % 100
  if (rest10 === 1 && rest100 !== 11) return 'поле'
  if (rest10 >= 2 && rest10 <= 4 && (rest100 < 12 || rest100 > 14)) return 'поля'
  return 'полей'
}

/**
 * Отказ площадки словами для человека.
 *
 * У ВБ есть полный разбор кода и HTTP-статуса (`wbErrorBadge`); у остальных
 * площадок — словарь отказов сборщика и исходный текст. Код и исходное
 * сообщение остаются рядом: по ним ищут в журнале и в поддержке.
 */
const describeRefusal = (
  card: Pick<PimMarketplaceCard, 'marketplace' | 'error' | 'last_error_code'>,
): { label: string; explanation: string | null; raw: string | null; code: string | null } => {
  const code = String(card.last_error_code ?? '').trim() || null
  const raw = String(card.error ?? '').trim() || null
  if (card.marketplace === 'wb') {
    const badge = wbErrorBadge(code, raw)
    return { label: badge.label, explanation: badge.hint ?? null, raw: null, code }
  }
  return {
    label: refusalHint(code) ? 'Отказ площадки' : 'Ошибка выгрузки',
    explanation: refusalHint(code),
    raw,
    code,
  }
}

/** Ссылка на окно: страница, на которой открыто окно, с ?card=<id>. */
const draftWindowLink = (cardId: number): string => {
  try {
    const url = new URL(window.location.href)
    url.searchParams.set('card', String(cardId))
    url.searchParams.delete('nm')
    url.searchParams.delete('offer')
    return url.toString()
  } catch {
    return `?card=${cardId}`
  }
}

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-zinc-400">
    {children}
  </h3>
)

/** Значение, которого нет: серое «нет данных» вместо прочерка. */
const NoData: React.FC<{ why?: string }> = ({ why }) => (
  <span className="text-gray-400 dark:text-zinc-500" title={why ? `${NO_DATA}: ${why}` : NO_DATA}>
    {NO_DATA}
  </span>
)

export const PimDraftCardModal: React.FC<PimDraftCardModalProps> = ({
  card,
  cards,
  onNavigate,
  onClose,
  onPublish,
  onSetExclusion,
  onDelete,
  canGoBack,
  onBack,
  totalCount,
}) => {
  const entityNav = useEntityNavigator()
  // Присутствие артикула на площадках — для переключателя ВБ · ОЗ · ЯМ · Сайт в цепочке.
  const presence = useProductPresence(card?.article)
  const permissions = useAuthStore((state) => state.user?.permissions ?? NO_PERMISSIONS)
  const canUpdate = hasPermission('Card:UPDATE', permissions)
  const canDelete = hasPermission('Card:DELETE', permissions)
  const [tab, setTab] = useState<PimDraftTab>('overview')
  const [activeImage, setActiveImage] = useState(0)
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)
  const [preview, setPreview] = useState<PimMarketplacePayloadPreview | null>(null)
  const [previewError, setPreviewError] = useState<string | null>(null)
  /** Ошибка действия (сохранение, словарь): не путается с ошибкой сборки. */
  const [actionError, setActionError] = useState<string | null>(null)
  const [loading, setLoading] = useState(Boolean(card))
  /**
   * Форма ручных правок.
   *
   * Заполняется из собранного payload, а не из «сырых» полей товара: в payload
   * правки уже наложены, поэтому человек правит ровно то, что уедет. Пустая
   * строка в поле означает «правки нет» — тогда уедет сгенерированное.
   */
  const [form, setForm] = useState({
    title: '', description: '', price: '',
    // Габариты — в сантиметрах, вес брутто — в граммах: сервис переводит их
    // в миллиметры сам, а человек мыслит теми единицами, что на упаковке.
    length: '', width: '', height: '', weightBrutto: '',
  })
  const [initialForm, setInitialForm] = useState(form)
  const [mediaUrls, setMediaUrls] = useState<string[]>([])
  const [initialMediaUrls, setInitialMediaUrls] = useState<string[]>([])
  /** Полный применимый набор характеристик, включая пустые и локальные поля PIM. */
  const [characteristicDefinitions, setCharacteristicDefinitions] = useState<EditableCharacteristic[]>([])
  const [characteristicScopeRef, setCharacteristicScopeRef] = useState('')
  const [categoryCharacteristicValues, setCategoryCharacteristicValues] = useState<Record<string, unknown>>({})
  const [characteristicsLoading, setCharacteristicsLoading] = useState(false)
  const [characteristicsError, setCharacteristicsError] = useState<string | null>(null)
  const [dictionaryOptions, setDictionaryOptions] = useState<Record<string, Array<{ value: string; label: string }>>>({})
  const [dictionaryLoading, setDictionaryLoading] = useState<Record<string, boolean>>({})
  /** Правки характеристик: charcID → нормализованный массив значений. */
  const [chars, setChars] = useState<Record<string, CharacteristicValueItem[]>>({})
  const [initialChars, setInitialChars] = useState<Record<string, CharacteristicValueItem[]>>({})
  const [saving, setSaving] = useState(false)
  /** Диалог «Несохранённые изменения» и отложенное действие (закрытие или листание). */
  const [unsavedOpen, setUnsavedOpen] = useState(false)
  const pendingAction = useRef<(() => void) | null>(null)
  const [technicalOpen, setTechnicalOpen] = useState(false)
  const [exclusionDialogOpen, setExclusionDialogOpen] = useState(false)
  const [exclusionReason, setExclusionReason] = useState('')
  const [exclusionBusy, setExclusionBusy] = useState(false)

  useEffect(() => {
    setTab('overview')
    setActiveImage(0)
    setPreview(null)
    setPreviewError(null)
    setActionError(null)
    if (!card) {
      setLoading(false)
      return undefined
    }

    const controller = new AbortController()
    setLoading(true)
    void pimMarketplaceCardsAPI.payloadPreview(card.seller_id, card.id, controller.signal)
      .then((result) => {
        if (!controller.signal.aborted) setPreview(result)
      })
      .catch((error) => {
        if (!controller.signal.aborted) setPreviewError(responseError(error))
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })
    return () => controller.abort()
  }, [card?.id, card?.seller_id])

  /**
   * Состав карточки читается из тех же локальных справочников и levels, что и
   * форма категории. Payload здесь только источник текущих значений, но не
   * перечня полей: пустая характеристика обязана оставаться редактируемой.
   */
  useEffect(() => {
    let cancelled = false
    setCharacteristicDefinitions([])
    setCharacteristicScopeRef('')
    setCategoryCharacteristicValues({})
    setDictionaryOptions({})
    setCharacteristicsError(null)
    if (!card?.category_id) {
      setCharacteristicsLoading(false)
      return undefined
    }

    setCharacteristicsLoading(true)
    void (async () => {
      const marketplace = card.marketplace
      const category = await categoryMpCharacteristicsAPI.get(card.category_id!, marketplace)
      const scopeRef = String(category.current_scope_ref?.[marketplace] || '')
      if (!scopeRef) throw new Error('У категории PIM не выбрана категория маркетплейса')

      // Я.Маркет: своя форма справочника, схемы mp_characteristic_mappings для
      // него на этом этапе ещё нет (см. applicableYmDefinitions) — ветка
      // отдельная, а не третий вариант тернарника WB/OZON.
      if (marketplace === 'ym') {
        const response = await yandexMarketAPI.getCategoryParameters(Number(scopeRef))
        const data = response.data?.data ?? response.data
        const parameters: YmCategoryParameter[] = Array.isArray(data?.parameters) ? data.parameters : []
        if (cancelled) return
        setCharacteristicScopeRef(scopeRef)
        setCharacteristicDefinitions(applicableYmDefinitions(parameters))
        setDictionaryOptions(Object.fromEntries(
          parameters
            .filter((row) => Array.isArray(row.values) && row.values.length > 0)
            .map((row) => [
              String(row.parameter_id),
              row.values.map((value) => ({ value: String(value.value_id), label: value.value })),
            ]),
        ))
        setCategoryCharacteristicValues(Object.fromEntries(
          category.values
            .filter((row) => row.marketplace === 'ym')
            .map((row) => [String(row.target_id), row.value]),
        ))
        return
      }

      const schemaPromise = marketplaceMappingAPI.getSchema(marketplace, scopeRef)
      const referencePromise = marketplace === 'wb'
        ? marketplaceReferenceAPI.getWbSubjectCharacteristics(Number(scopeRef))
        : (() => {
            const [descriptionCategoryId, typeId] = scopeRef.split(':').map(Number)
            if (!descriptionCategoryId || !typeId) {
              throw new Error(`Некорректная пара категории OZON: ${scopeRef}`)
            }
            return marketplaceReferenceAPI.getOzonCategoryAttributes(descriptionCategoryId, typeId)
          })()
      const [schema, reference] = await Promise.all([schemaPromise, referencePromise])
      if (cancelled) return
      setCharacteristicScopeRef(scopeRef)
      setCharacteristicDefinitions(applicableDefinitions(
        marketplace,
        reference,
        schema.levels ?? [],
      ))
      setCategoryCharacteristicValues(Object.fromEntries(
        category.values
          .filter((row) => row.marketplace === marketplace)
          .map((row) => [String(row.target_id), row.value]),
      ))
    })()
      .catch((error) => {
        if (!cancelled) setCharacteristicsError(responseError(error))
      })
      .finally(() => {
        if (!cancelled) setCharacteristicsLoading(false)
      })
    return () => { cancelled = true }
  }, [card?.id, card?.category_id, card?.marketplace])

  const platformPayload = preview?.payload || null
  const ozonItem = card?.marketplace === 'ozon' ? platformPayload : null
  const wbCardUpload = card?.marketplace === 'wb' ? platformPayload?.card_upload : null
  const wbVariant = wbCardUpload?.variants?.[0] || null
  /** Я.Маркет: `payload` — плоский `YmOutboundOffer` (offer-mappings/update), как и у OZON. */
  const ymOffer = card?.marketplace === 'ym' ? platformPayload : null
  // ОЗОН и Я.Маркет отдают плоский объект с одинаковыми именами полей (name/
  // description) — здесь это не тернарник «wb или всё остальное», а общая
  // форма двух площадок; у ВБ поля лежат во вложенном variants[0].
  const outboundTitle = card?.marketplace === 'wb' ? wbVariant?.title : platformPayload?.name
  const outboundDescription = card?.marketplace === 'wb' ? wbVariant?.description : platformPayload?.description
  const outboundCharacteristics: any[] = card?.marketplace === 'ozon'
    ? (Array.isArray(ozonItem?.attributes) ? ozonItem.attributes : [])
    : card?.marketplace === 'ym'
      ? (Array.isArray(ymOffer?.parameterValues) ? ymOffer.parameterValues : [])
      : (Array.isArray(wbVariant?.characteristics) ? wbVariant.characteristics : [])
  const rawMedia = preview?.media_urls || []
  const resolvedImages = useMemo(
    () => rawMedia.map((image) => getMediaUrl(image, Number(card?.design_code) || undefined)),
    [card?.design_code, rawMedia],
  )
  /** Правимый набор: сетка «Медиа» показывает его, а не ответ сервера. */
  const editableImages = useMemo(
    () => mediaUrls.map((image) => getMediaUrl(image, Number(card?.design_code) || undefined)),
    [card?.design_code, mediaUrls],
  )

  /**
   * Форма заполняется из собранного тела отправки: в нём ручные правки уже
   * наложены, поэтому человек видит и правит ровно то, что уедет. Возврат к
   * сгенерированному делается кнопкой «Сбросить правки», а не очисткой поля.
   */
  useEffect(() => {
    if (!preview) return
    // Я.Маркет: цена не входит в оффер (offer-mappings/update) — коммерция
    // уходит отдельным шагом offer-prices/updates уже после создания оффера
    // (SPEC_2026-09-13_YANDEX_MARKET.md §4.2), поэтому здесь её не показываем.
    const price = card?.marketplace === 'ozon'
      ? preview.payload?.price
      : card?.marketplace === 'ym'
        ? null
        : preview.payload?.card_upload?.variants?.[0]?.sizes?.[0]?.price
    /*
      Габариты у площадок в разных единицах: у ВБ сантиметры и килограммы, у
      Ozon миллиметры и граммы, у Я.Маркета — сразу сантиметры и килограммы
      (YmOutboundOffer.weightDimensions). Правятся они в сантиметрах и
      граммах — так их читают с упаковки, — обратный перевод делает сервис.
    */
    const wbDimensions = preview.payload?.card_upload?.variants?.[0]?.dimensions
    const ymDimensions = preview.payload?.weightDimensions
    const dimensions = card?.marketplace === 'ozon'
      ? {
          length: preview.payload?.depth ? Number(preview.payload.depth) / 10 : '',
          width: preview.payload?.width ? Number(preview.payload.width) / 10 : '',
          height: preview.payload?.height ? Number(preview.payload.height) / 10 : '',
          weightBrutto: preview.payload?.weight ?? '',
        }
      : card?.marketplace === 'ym'
        ? {
            length: ymDimensions?.length ?? '',
            width: ymDimensions?.width ?? '',
            height: ymDimensions?.height ?? '',
            // У Я.Маркета вес в килограммах, правка — в граммах.
            weightBrutto: ymDimensions?.weight
              ? Math.round(Number(ymDimensions.weight) * 1000)
              : '',
          }
        : {
            length: wbDimensions?.length ?? '',
            width: wbDimensions?.width ?? '',
            height: wbDimensions?.height ?? '',
            // У ВБ вес брутто в килограммах, правка — в граммах.
            weightBrutto: wbDimensions?.weightBrutto
              ? Math.round(Number(wbDimensions.weightBrutto) * 1000)
              : '',
          }
    const next = {
      title: String(outboundTitle ?? ''),
      description: String(outboundDescription ?? ''),
      price: price == null ? '' : String(price),
      length: String(dimensions.length ?? ''),
      width: String(dimensions.width ?? ''),
      height: String(dimensions.height ?? ''),
      weightBrutto: String(dimensions.weightBrutto ?? ''),
    }
    setForm(next)
    setInitialForm(next)
    setMediaUrls(rawMedia)
    setInitialMediaUrls(rawMedia)
    // Набор полей задаёт справочник, а значения накладываются слоями:
    // ручная правка → payload → категория → пусто. Важно проверять наличие
    // ключа override, а не truthiness: пустая правка сознательно подавляет
    // унаследованное категорийное значение.
    // Я.Маркет называет id характеристики `parameterId` (YmOutboundOffer.parameterValues),
    // а не `id`, как WB/OZON — оба варианта читаются здесь без падения.
    const outboundById = new Map(
      outboundCharacteristics
        .map((item) => [item?.id ?? item?.parameterId, item] as const)
        .filter((entry): entry is [number | string, any] => entry[0] !== null && entry[0] !== undefined)
        .map(([id, item]) => [String(id), item] as const),
    )
    const overrides = preview.overrides?.characteristics || {}
    const nextChars: Record<string, CharacteristicValueItem[]> = {}
    for (const definition of characteristicDefinitions) {
      const id = String(definition.targetId)
      const raw = Object.prototype.hasOwnProperty.call(overrides, id)
        ? overrides[id]
        : outboundById.has(id)
          ? outboundById.get(id)
          : categoryCharacteristicValues[id]
      nextChars[id] = rawCharacteristicItems(raw, definition.multiple)
    }
    setChars(nextChars)
    setInitialChars(nextChars)
  }, [
    preview,
    card?.marketplace,
    outboundTitle,
    outboundDescription,
    rawMedia,
    characteristicDefinitions,
    categoryCharacteristicValues,
  ])

  const reload = async () => {
    if (!card) return
    setLoading(true)
    try {
      setPreview(await pimMarketplaceCardsAPI.payloadPreview(card.seller_id, card.id))
      setPreviewError(null)
    } catch (error) {
      setPreviewError(responseError(error))
    } finally {
      setLoading(false)
    }
  }

  /**
   * Сохранение правок. Наружу ничего не уходит — бэкенд пишет новую ревизию,
   * поэтому подтверждения здесь нет: подтверждают отправку, а не подготовку.
   * После сохранения тело пересобирается, и человек видит результат правки.
   */
  const saveOverrides = async (overrides: Record<string, unknown>): Promise<boolean> => {
    if (!card) return false
    setSaving(true)
    try {
      await pimMarketplaceCardsAPI.saveOverrides(card.seller_id, card.id, overrides as any)
      setActionError(null)
      await reload()
      return true
    } catch (error) {
      setActionError(responseError(error))
      return false
    } finally {
      setSaving(false)
    }
  }

  if (!card) return null
  const overriddenFields = new Set(Object.keys(preview?.overrides || {}))
  const overriddenChars = new Set(Object.keys(preview?.overrides?.characteristics || {}))
  const isMediaCharacteristic = (definition: EditableCharacteristic) => {
    switch (card.marketplace) {
      case 'ozon':
        return OZON_MEDIA_CHARACTERISTIC_IDS.has(definition.targetId)
      case 'wb':
        return WB_MEDIA_CHARACTERISTIC_IDS.has(definition.targetId)
      case 'ym':
        // Я.Маркет: медийных характеристик (видео/rich-контент) пока не заводим.
        return false
      default:
        return assertNever(card.marketplace)
    }
  }
  const mediaCharacteristicDefinitions = characteristicDefinitions.filter(isMediaCharacteristic)
  /**
   * Состав вкладки «Характеристики» — без медийных.
   *
   * Видео, обложка, rich-контент и локальные поля ПИМ редактируются на вкладке
   * «Медиа», рядом с визуалами, к которым они относятся. Раньше они попадали в
   * оба списка, и оператор видел одно поле дважды: правка в одном месте молча
   * меняла значение в другом. Счётчик вкладки считает то же, что в ней лежит.
   */
  const contentCharacteristicDefinitions = characteristicDefinitions.filter(
    (definition) => !isMediaCharacteristic(definition),
  )

  const loadDictionary = async (definition: EditableCharacteristic, search: string) => {
    // Я.Маркет отдаёт весь словарь параметра сразу (dictionaryOptions уже
    // заполнен из getCategoryParameters) — поискового экшена по WB/OZON API
    // здесь звать не нужно и нечем: characteristicField не подключает
    // onSearch для ym (ниже), это дополнительная защита от будущей правки.
    if (!definition.dictionaryCode || search.trim().length < 2 || card.marketplace === 'ym') return
    const id = String(definition.targetId)
    setDictionaryLoading((current) => ({ ...current, [id]: true }))
    try {
      const found = card.marketplace === 'wb'
        ? await marketplaceReferenceAPI.getWbDictionaryValues(definition.dictionaryCode, search)
        : await (() => {
            const [descriptionCategoryId, typeId] = characteristicScopeRef.split(':').map(Number)
            return marketplaceReferenceAPI.getOzonAttributeValues(
              descriptionCategoryId,
              typeId,
              definition.targetId,
              search,
            )
          })()
      setDictionaryOptions((current) => {
        const merged = new Map(
          (current[id] || []).map((option) => [`${option.value}:${option.label}`, option]),
        )
        for (const option of found) {
          const normalized = {
            value: String(option.value_id ?? option.value),
            label: option.value,
          }
          merged.set(`${normalized.value}:${normalized.label}`, normalized)
        }
        return { ...current, [id]: [...merged.values()] }
      })
    } catch (error) {
      setActionError(responseError(error))
    } finally {
      setDictionaryLoading((current) => ({ ...current, [id]: false }))
    }
  }

  const resetCharacteristicOverride = async (targetId: number) => {
    const remaining = { ...(preview?.overrides?.characteristics || {}) }
    delete remaining[String(targetId)]
    await saveOverrides({ characteristics: remaining })
  }

  const characteristicField = (definition: EditableCharacteristic) => {
    const id = String(definition.targetId)
    const values = chars[id] || []
    const categoryValue = categoryCharacteristicValues[id]
    const inherited = !overriddenChars.has(id)
      && categoryValue !== null
      && categoryValue !== undefined
      && String(categoryValue).trim() !== ''
    const apiHint = `id ${id}${CHARACTERISTIC_HINTS[id] ? ` · ${CHARACTERISTIC_HINTS[id]}` : ''}`
    return (
      <Kv key={id} label={definition.name} apiKey={apiHint}>
        <div className="space-y-1.5">
          <div className="flex flex-wrap items-center gap-1.5">
            <Badge variant={definition.level === 'product' ? 'purple' : 'blue'}>
              {definition.level === 'product' ? 'товарная' : 'категорийная'}
            </Badge>
            {definition.required && <Badge variant="yellow">обязательная</Badge>}
            {inherited && <Badge variant="gray">из категории</Badge>}
            {definition.localOnly && <Badge variant="gray">только PIM</Badge>}
          </div>
          {definition.targetId === 9070 && card.marketplace === 'ozon' ? (
            <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-zinc-200">
              <input
                id={`card-characteristic-${id}`}
                aria-label={definition.name}
                type="checkbox"
                checked={['true', '1', 'да'].includes(String(values[0]?.value || '').toLowerCase())}
                onChange={(event) => setChars((current) => ({
                  ...current,
                  [id]: [{ value: event.target.checked ? 'true' : 'false', valueId: null }],
                }))}
                className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              Да
            </label>
          ) : (
            <CharacteristicValueInput
              id={`card-characteristic-${id}`}
              aria-label={definition.name}
              values={values}
              onChange={(next) => setChars((current) => ({ ...current, [id]: next }))}
              multiple={definition.multiple}
              maxCount={definition.maxCount}
              options={definition.dictionaryCode ? (dictionaryOptions[id] || []) : undefined}
              // Я.Маркет отдаёт полный словарь параметра сразу — ComboBox фильтрует
              // его на месте, без обращения к сети (см. loadDictionary выше).
              onSearch={definition.dictionaryCode && card.marketplace !== 'ym'
                ? (search) => void loadDictionary(definition, search)
                : undefined}
              loading={dictionaryLoading[id] === true}
              inputType={definition.targetId === 23518 || definition.number ? 'number' : 'text'}
              numberStep={definition.targetId === 23518 ? 1 : 'any'}
              multiline={LONG_TEXT_CHARACTERISTIC_IDS.has(definition.targetId)}
              placeholder={definition.targetId === 23518 ? 'Введите целое число' : undefined}
              emptyText="Введите минимум 2 символа"
            />
          )}
          {definition.localOnly && (
            <p className="text-xs text-amber-700 dark:text-amber-400">
              В выгрузку не уходит: способа отправки в API площадки нет.
            </p>
          )}
          {overriddenChars.has(id) && (
            <div className="flex items-center justify-between gap-2">
              <ManualMark />
              <Button
                variant="ghost"
                size="sm"
                icon={<RotateCcw className="h-3.5 w-3.5" />}
                loading={saving}
                disabled={!canUpdate}
                title={canUpdate ? undefined : NEED_UPDATE_RIGHT}
                onClick={() => void resetCharacteristicOverride(definition.targetId)}
              >
                Вернуть значение категории
              </Button>
            </div>
          )}
        </div>
      </Kv>
    )
  }
  const dimensionsDirty = DIMENSION_FIELDS.some(({ key }) => form[key] !== initialForm[key])
  const dirtyLabels = [
    ...(form.title !== initialForm.title ? ['наименование'] : []),
    ...(form.description !== initialForm.description ? ['описание'] : []),
    ...(form.price !== initialForm.price ? ['цена'] : []),
    ...(JSON.stringify(mediaUrls) !== JSON.stringify(initialMediaUrls) ? ['визуалы'] : []),
    ...(JSON.stringify(chars) !== JSON.stringify(initialChars) ? ['характеристики'] : []),
    ...(dimensionsDirty ? ['габариты'] : []),
  ]
  const hasUnsaved = dirtyLabels.length > 0
  /** Закрытие и листание при несохранённых правках спрашивают, а не сбрасывают форму молча. */
  const guard = (action: () => void) => {
    if (!hasUnsaved) return action()
    pendingAction.current = action
    setUnsavedOpen(true)
  }
  const submitOverrides = (): Promise<boolean> => {
    const wholesaleMinimum = chars['23518']?.[0]?.value.trim() ?? ''
    if (card.marketplace === 'ozon'
      && wholesaleMinimum
      && !/^-?\d+$/.test(wholesaleMinimum)) {
      setActionError('«Минимальное количество оптом» должно быть целым числом')
      return Promise.resolve(false)
    }
    const overrides: Record<string, unknown> = {}
    if (form.title.trim()) overrides.title = form.title.trim()
    if (form.description.trim()) overrides.description = form.description
    const price = Number(form.price)
    if (Number.isFinite(price) && price > 0) overrides.price = price
    overrides.media_urls = mediaUrls
    // Отправляем только изменённые характеристики: остальные должны остаться
    // сгенерированными, иначе правкой стал бы весь набор целиком.
    const definitionById = new Map(
      characteristicDefinitions.map((definition) => [String(definition.targetId), definition]),
    )
    const changedChars: Record<string, unknown> = {}
    for (const [id, value] of Object.entries(chars)) {
      if (JSON.stringify(value) !== JSON.stringify(initialChars[id] || [])) {
        changedChars[id] = characteristicRawValue(value, definitionById.get(id)?.multiple === true)
      }
    }
    const keptChars = { ...(preview?.overrides?.characteristics || {}), ...changedChars }
    if (Object.keys(keptChars).length > 0) overrides.characteristics = keptChars
    // Габариты уходят набором: отправлять их по одному значит слать половину
    // размера, а сервис ждёт согласованную тройку с весом.
    const dimensions: Record<string, number> = {}
    for (const { key } of DIMENSION_FIELDS) {
      const value = Number(form[key])
      if (Number.isFinite(value) && value > 0) dimensions[key] = value
    }
    if (Object.keys(dimensions).length > 0) overrides.dimensions = dimensions
    return saveOverrides(overrides)
  }
  const cancelEdits = () => {
    setForm(initialForm)
    setMediaUrls(initialMediaUrls)
    setChars(initialChars)
  }

  const status = draftStateInfo(card)
  const profile = marketplaceProfile(card.marketplace)
  const marketplaceName = profile.fullName
  const validationErrors = preview?.validation_errors || []
  // Я.Маркет: цена не входит в оффер — уходит отдельным шагом коммерции
  // (см. useEffect выше), поэтому в предпросмотре её показывать нечем.
  const outboundPrice = card.marketplace === 'ozon'
    ? ozonItem?.price
    : card.marketplace === 'ym'
      ? null
      : wbVariant?.sizes?.[0]?.price
  const hasPrice = Number(outboundPrice) > 0
  const priceWhy = card.marketplace === 'ym'
    ? 'у Я.Маркета цена уходит отдельным шагом после создания оффера'
    : preview ? 'в теле отправки цены нет' : 'тело отправки ещё не собрано'

  // ── Выводы для полосы «Что дальше» ────────────────────────────────────────
  const submitState = String(card.submit_state ?? '').trim().toLowerCase()
  const published = Boolean(String(card.external_id ?? '').trim())
  const excluded = Boolean(card.publish_excluded_at)
  const refused = isRefusedCard(card)
  const unanswered = submitState === 'sent' && !published
  const publishable = isPublishableCard(card)
  const buildBlocked = !loading && (Boolean(previewError) || validationErrors.length > 0 || (!preview?.exact && Boolean(preview)))
  const refusal = refused ? describeRefusal(card) : null
  const retryLabel = refused || unanswered
  const publishLabel = retryLabel ? 'Повторить выгрузку' : `Выгрузить на ${profile.label}`

  const requiredDefinitions = contentCharacteristicDefinitions.filter((definition) => definition.required)
  const isFilled = (definition: EditableCharacteristic) =>
    (chars[String(definition.targetId)] || []).some((item) => item.value.trim() !== '')
  const requiredFilled = requiredDefinitions.filter(isFilled).length
  const totalFilled = contentCharacteristicDefinitions.filter(isFilled).length
  // Сколько характеристик реально едет — считаем по телу отправки, а не по справочнику:
  // справочник нужен только чтобы знать, сколько всего их бывает и какие обязательные.
  const payloadFilled = outboundCharacteristics.length > 0
    ? outboundCharacteristics.length
    : Object.keys(preview?.characteristic_sources ?? {}).length
  const definitionsKnown = !characteristicsLoading && !characteristicsError && contentCharacteristicDefinitions.length > 0
  const filledCount = definitionsKnown ? totalFilled : payloadFilled
  const filledText = definitionsKnown
    ? `${totalFilled} из ${contentCharacteristicDefinitions.length}`
    : String(payloadFilled)

  const publishDisabledReason = (): string | undefined => {
    if (!canUpdate) return NEED_UPDATE_RIGHT
    if (loading) return 'Идёт сборка payload'
    if (!preview?.exact) return 'Сначала исправьте ошибки сборки payload'
    if (excluded) return 'Карточка исключена из выгрузки: верните её в «Ещё»'
    if (hasUnsaved) return 'Есть несохранённые правки: сохраните или отмените их'
    return undefined
  }
  const doPublish = () => onPublish?.(card)

  const sentAt = formatDraftDateTime(card.updated_at)
  const stateTone: NextStepTone = loading
    ? 'neutral'
    : buildBlocked
      ? 'bad'
      : refused
        ? 'bad'
        : excluded
          ? 'warn'
          : unanswered
            ? 'warn'
            : published
              ? 'ok'
              : submitState === 'in_flight'
                ? 'info'
                : 'ok'
  const stateTitle = loading
    ? `Собираем фактический payload ${marketplaceName}`
    : buildBlocked
      ? `Карточка не собрана и на ${profile.label} не уйдёт`
      : refused
        ? `${marketplaceName} отказал в карточке`
        : excluded
          ? 'Карточка исключена из выгрузки'
          : unanswered
            ? 'Площадка не ответила на прошлую выгрузку'
            : published
              ? `Карточка на площадке: ${card.external_id}`
              : submitState === 'in_flight'
                ? 'Отправка идёт'
                : 'Всё собрано, можно выгружать'
  const stateHint = loading
    ? 'Предпросмотр собирается той же логикой и из той же замороженной ревизии, что и реальная публикация.'
    : buildBlocked
      ? (previewError ?? 'Исправьте блокеры сборки, перечисленные ниже, и подготовьте карточку заново.')
      : refused
        ? refusal?.label
        : excluded
          ? (card.publish_excluded_reason
            ? `Причина: ${card.publish_excluded_reason}`
            : 'Выгрузка запрещена вручную. Вернуть карточку в выгрузку можно в «Ещё».')
          : unanswered
            ? `Запрос ушёл, ответа нет. Последняя запись по карточке: ${sentAt}.${status.hint ? ` ${status.hint}` : ''}`
            : published
              ? 'Выгрузка не требуется.'
              : submitState === 'in_flight'
                ? 'Запрос к площадке выполняется прямо сейчас.'
                : `Ревизия${preview?.source?.card_revision ? ` #${preview.source.card_revision}` : ''} заморожена, тело отправки совпадает с тем, что уйдёт на ${profile.label}.`

  const errorPlate = !loading && (buildBlocked || refused) ? (
    <div className="space-y-2">
      {previewError && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-950 dark:bg-red-950/40 dark:text-red-300">
          {previewError}
        </div>
      )}
      {validationErrors.length > 0 && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-950 dark:bg-amber-950/40 dark:text-amber-200">
          {validationErrors.map((error) => (
            <div key={`${error.code}-${error.message}`} className="mt-1 first:mt-0">
              <div>{refusalHint(error.code) || error.message}</div>
              <div className="mt-0.5 font-mono text-[10px] opacity-70">
                {error.code}
                {refusalHint(error.code) ? ` · ${error.message}` : ''}
              </div>
            </div>
          ))}
        </div>
      )}
      {refused && refusal && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-950 dark:bg-red-950/40 dark:text-red-300">
          <div className="font-medium">{refusal.label}</div>
          {(refusal.explanation || refusal.raw) && (
            <div className="mt-0.5 line-clamp-3 whitespace-pre-line text-xs">
              {refusal.explanation || refusal.raw}
            </div>
          )}
          {refusal.code && (
            <div className="mt-0.5 font-mono text-[10px] opacity-70">{refusal.code}</div>
          )}
        </div>
      )}
    </div>
  ) : undefined

  const checks: NextStepCheck[] = [
    loading
      ? { key: 'build', label: 'Сборка', tone: 'none', title: 'payload ещё собирается', onClick: () => setTab('overview') }
      : buildBlocked
        ? {
            key: 'build',
            label: 'Сборка',
            value: validationErrors.length ? `ошибок: ${validationErrors.length}` : 'не готова',
            tone: 'bad',
            onClick: () => setTab('overview'),
          }
        : { key: 'build', label: 'Сборка', value: 'готова', tone: 'ok', onClick: () => setTab('overview') },
    !preview
      ? { key: 'text', label: 'Текст', tone: 'none', title: 'тело отправки не собрано', onClick: () => setTab('text') }
      : form.title.trim() && form.description.trim()
        ? { key: 'text', label: 'Текст', value: 'есть', tone: 'ok', onClick: () => setTab('text') }
        : { key: 'text', label: 'Текст', value: 'не хватает', tone: 'bad', onClick: () => setTab('text') },
    !preview
      ? { key: 'chars', label: 'Характеристики', tone: 'none' as const, title: 'тело отправки не собрано', onClick: () => setTab('characteristics') }
      : definitionsKnown && requiredDefinitions.length > 0
        ? {
            key: 'chars',
            label: 'Характеристики',
            value: `${requiredFilled}/${requiredDefinitions.length} обяз.`,
            tone: requiredFilled < requiredDefinitions.length ? 'bad' as const : 'ok' as const,
            onClick: () => setTab('characteristics'),
          }
        : {
            key: 'chars',
            label: 'Характеристики',
            value: filledText,
            tone: filledCount > 0 ? 'ok' as const : 'warn' as const,
            // Обязательные не проверить, пока справочник не загрузился: заполненность при этом известна.
            title: definitionsKnown
              ? undefined
              : characteristicsLoading
                ? 'справочник загружается: обязательные пока не проверены'
                : characteristicsError
                  ? `справочник не загрузился (${characteristicsError}): обязательные не проверены`
                  : 'у категории не выбран набор характеристик площадки: обязательные не проверены',
            onClick: () => setTab('characteristics'),
          },
    !preview
      ? { key: 'media', label: 'Визуалы', tone: 'none', title: 'тело отправки не собрано', onClick: () => setTab('media') }
      : { key: 'media', label: 'Визуалы', value: mediaUrls.length, tone: mediaUrls.length > 0 ? 'ok' : 'bad', onClick: () => setTab('media') },
    hasPrice
      ? { key: 'price', label: 'Цена', value: money(outboundPrice), tone: 'ok', onClick: () => setTab('text') }
      : card.marketplace === 'ym' || !preview
        ? { key: 'price', label: 'Цена', tone: 'none', title: priceWhy, onClick: () => setTab('text') }
        : { key: 'price', label: 'Цена', value: 'не задана', tone: 'bad', onClick: () => setTab('text') },
    card.merge_label
      ? { key: 'merge', label: 'Склейка', value: card.merge_label, tone: 'ok', onClick: () => setTab('merge') }
      : { key: 'merge', label: 'Склейка', value: 'нет', tone: 'info', onClick: () => setTab('merge') },
    {
      key: 'submit',
      label: 'Отправка',
      value: published
        ? 'на площадке'
        : submitState === 'rejected'
          ? 'отказ'
          : submitState === 'sent'
            ? 'без ответа'
            : submitState === 'in_flight'
              ? 'идёт'
              : 'не было',
      tone: published
        ? 'ok'
        : submitState === 'rejected'
          ? 'bad'
          : submitState === 'sent'
            ? 'warn'
            : 'info',
      onClick: () => setTab('overview'),
    },
  ]

  const nextStepActions: NextStepAction[] = []
  if (onPublish && publishable) {
    nextStepActions.push({
      key: 'publish',
      label: publishLabel,
      icon: <CloudUpload className="h-4 w-4" />,
      onClick: doPublish,
      disabledReason: publishDisabledReason(),
    })
  }
  if (excluded && onSetExclusion) {
    nextStepActions.push({
      key: 'return',
      label: 'Вернуть в выгрузку',
      variant: 'secondary',
      icon: <RotateCcw className="h-4 w-4" />,
      onClick: () => void onSetExclusion(card, false),
      disabledReason: canUpdate ? undefined : NEED_UPDATE_RIGHT,
    })
  }

  // ── Шапка ─────────────────────────────────────────────────────────────────
  const stateBadge = (
    <span title={status.hint}>
      <Badge variant={status.variant}>{status.label}</Badge>
    </span>
  )
  const buildBadge = (
    <Badge variant={preview?.exact ? 'green' : loading ? 'gray' : 'red'}>
      {preview?.exact ? 'Сборка готова' : loading ? 'Идёт сборка' : 'Не собрано'}
    </Badge>
  )
  const excludedBadge = excluded ? (
    <span title={card.publish_excluded_reason ? `Причина: ${card.publish_excluded_reason}` : 'Выгрузка запрещена вручную'}>
      <Badge variant="yellow">Не выгружать</Badge>
    </span>
  ) : null
  const sellerLabel = card.seller_name || card.seller_short_name || card.cabinet || `ID ${card.seller_id}`
  const headerTitle = form.title.trim() || String(outboundTitle ?? '') || card.seo_title || card.title || 'Наименование не задано'
  const noDesign = isNoDesignCode(card.design_code)
  const designCode = noDesign ? null : card.design_code
  const metaItems: IdMetaItem[] = [
    ...(noDesign ? [{ label: 'Макет', value: 'Без макета' }] : []),
    { label: 'Кабинет', value: sellerLabel },
    {
      label: 'Ревизия',
      value: preview?.source?.card_revision ? `#${preview.source.card_revision}` : null,
      mono: true,
    },
    {
      label: 'SEO',
      value: preview?.source?.seo_revision_id
        ? `#${preview.source.seo_revision_id}${preview.source.seo_revision ? ` · v${preview.source.seo_revision}` : ''}`
        : null,
      mono: true,
    },
    { label: 'Склейка', value: card.merge_label },
    { label: 'Внешний ID', value: card.external_id, mono: true, copy: true },
  ]

  // ── Цепочка ───────────────────────────────────────────────────────────────
  const chainItems: ChainItem[] = [
    designCode
      ? {
          key: 'design',
          label: 'Макет',
          sub: `#${designCode}`,
          tone: 'ok',
          onClick: entityNav.available
            ? () => entityNav.open({ kind: 'design', code: designCode })
            : undefined,
        }
      : { key: 'design', label: noDesign ? 'Без макета' : 'Макет', tone: 'none', disabled: true, title: noDesign ? 'у товара нет макета' : 'макет карточки не определён' },
    {
      key: 'product',
      label: 'Товар',
      sub: card.article,
      tone: 'ok',
      onClick: entityNav.available
        ? () => entityNav.open({ kind: 'product', article: card.article })
        : undefined,
    },
    { key: 'draft', label: 'Черновик', sub: `#${card.id}`, current: true, tone: stateTone === 'bad' ? 'bad' : stateTone === 'warn' ? 'warn' : 'ok' },
    buildPresenceChain({
      entries: presence.entries,
      article: card.article,
      open: entityNav.available ? entityNav.open : undefined,
      current: { marketplace: card.marketplace, sellerId: card.seller_id },
      guard,
    }),
    published
      ? {
          key: 'mp_card',
          label: `Карточка ${profile.label}`,
          sub: String(card.external_id),
          tone: 'ok',
          onClick: entityNav.available
            ? () => entityNav.open({
                kind: 'mp_card',
                marketplace: card.marketplace,
                sellerId: card.seller_id,
                key: card.marketplace === 'wb' ? String(card.external_id) : card.article,
              })
            : undefined,
        }
      : {
          key: 'mp_card',
          label: `Карточка ${profile.label}`,
          tone: 'none',
          disabled: true,
          title: 'появится после выгрузки',
        },
  ]

  // ── Меню «Ещё» ────────────────────────────────────────────────────────────
  const copyText = async (text: string, what: string) => {
    const ok = await copyToClipboard(text)
    const { showToast } = useToastStore.getState()
    if (ok) showToast(`Скопировано: ${what}`, 'success')
    else showToast('Не удалось скопировать', 'error')
  }
  const deleteBlockedReason = !canDelete
    ? NEED_DELETE_RIGHT
    : published
      ? 'Выгруженную карточку удалить нельзя'
      : submitState === 'in_flight'
        ? 'Отправка идёт: удалить нельзя'
        : undefined
  const moreMenu: EntityMoreMenuItem[] = [
    {
      key: 'copy-article',
      label: 'Скопировать артикул',
      icon: <Copy className="h-4 w-4" />,
      hotkey: 'C',
      onClick: () => void copyText(card.article, 'артикул'),
    },
    {
      key: 'copy-link',
      label: 'Скопировать ссылку',
      icon: <Link2 className="h-4 w-4" />,
      hotkey: 'Shift+C',
      onClick: () => void copyText(draftWindowLink(card.id), 'ссылка'),
    },
    { divider: true },
    ...(onSetExclusion
      ? [excluded
        ? {
            key: 'exclusion',
            label: 'Вернуть в выгрузку',
            icon: <RotateCcw className="h-4 w-4" />,
            disabledReason: canUpdate ? undefined : NEED_UPDATE_RIGHT,
            onClick: () => void onSetExclusion(card, false),
          }
        : {
            key: 'exclusion',
            label: 'Не выгружать',
            icon: <Ban className="h-4 w-4" />,
            disabledReason: canUpdate ? undefined : NEED_UPDATE_RIGHT,
            onClick: () => {
              setExclusionReason('')
              setExclusionDialogOpen(true)
            },
          }]
      : []),
    {
      key: 'reset',
      label: 'Сбросить ручные правки',
      icon: <RotateCcw className="h-4 w-4" />,
      disabledReason: !canUpdate
        ? NEED_UPDATE_RIGHT
        : overriddenFields.size === 0
          ? 'Ручных правок нет'
          : undefined,
      onClick: () => void saveOverrides({}),
    },
    {
      key: 'technical',
      label: 'Технические данные',
      icon: <Wrench className="h-4 w-4" />,
      onClick: () => setTechnicalOpen(true),
    },
    ...(onDelete
      ? [{
          key: 'delete',
          label: 'Удалить черновик',
          icon: <Trash2 className="h-4 w-4" />,
          danger: true,
          disabledReason: deleteBlockedReason,
          onClick: () => onDelete(card),
        }]
      : []),
  ]

  /** Точка у вкладки с проблемой: бейдж «Не собрано», нет текста или визуалов, не хватает обязательных. */
  const tabAttention: Partial<Record<PimDraftTab, 'warn' | 'bad'>> = loading || !preview
    ? {}
    : {
        overview: buildBlocked || refused ? 'bad' : undefined,
        text: form.title.trim() && form.description.trim() ? undefined : 'bad',
        characteristics: definitionsKnown && requiredDefinitions.length > 0 && requiredFilled < requiredDefinitions.length
          ? 'bad'
          : filledCount === 0 ? 'warn' : undefined,
        media: mediaUrls.length === 0 ? 'bad' : undefined,
      }

  const tabs: Array<{ key: PimDraftTab; label: string; icon: React.ReactNode }> = [
    { key: 'overview', label: 'Обзор', icon: <Eye className="h-4 w-4" /> },
    { key: 'text', label: 'Текст', icon: <FileText className="h-4 w-4" /> },
    { key: 'characteristics', label: 'Характеристики', icon: <ListChecks className="h-4 w-4" /> },
    { key: 'media', label: 'Медиа', icon: <Images className="h-4 w-4" /> },
    { key: 'merge', label: 'Склейка', icon: <Layers className="h-4 w-4" /> },
    { key: 'history', label: 'История', icon: <History className="h-4 w-4" /> },
  ]

  const saveDisabledReason = !canUpdate
    ? NEED_UPDATE_RIGHT
    : !hasUnsaved
      ? 'Нет несохранённых правок'
      : undefined

  const footerState = hasUnsaved ? (
    <span className="flex items-center gap-1.5 text-xs text-amber-700 dark:text-amber-400">
      <span className="inline-block h-1.5 w-1.5 rounded-full bg-amber-500" />
      Изменено: {dirtyLabels.join(', ')}
    </span>
  ) : overriddenFields.size > 0 ? (
    <span className="text-xs text-amber-700 dark:text-amber-400">
      Правлено вручную: {overriddenFields.size} {pluralFields(overriddenFields.size)}
    </span>
  ) : (
    <span className="text-xs text-gray-500 dark:text-zinc-400">
      Состав как собрал конвейер
      {preview?.source?.card_revision ? ` · ревизия #${preview.source.card_revision} заморожена` : ''}
    </span>
  )

  const dimensionsText = [form.length, form.width, form.height].every((value) => value !== '')
    ? `${form.length} × ${form.width} × ${form.height} см`
    : null
  const brand = card.marketplace === 'wb'
    ? wbVariant?.brand
    : card.marketplace === 'ozon'
      ? ozonItem?.vendor
      : ymOffer?.vendor
  const barcodes = card.marketplace === 'wb'
    ? (wbVariant?.sizes?.[0]?.skus ?? []).join(', ')
    : card.marketplace === 'ozon'
      ? String(ozonItem?.barcode ?? '')
      : Array.isArray(ymOffer?.barcodes) ? ymOffer.barcodes.join(', ') : ''
  const sourceCounts = Object.values(preview?.characteristic_sources ?? {}).reduce<Record<string, number>>(
    (acc, source) => ({ ...acc, [source]: (acc[source] || 0) + 1 }),
    {},
  )
  const charSourcesText = Object.keys(sourceCounts).length > 0 || overriddenChars.size > 0
    ? [
        sourceCounts.seo ? `из SEO: ${sourceCounts.seo}` : null,
        sourceCounts.category ? `из категории: ${sourceCounts.category}` : null,
        sourceCounts.product ? `товарные: ${sourceCounts.product}` : null,
        overriddenChars.size ? `вручную: ${overriddenChars.size}` : null,
      ].filter(Boolean).join(' · ')
    : null

  const rows: Array<[string, React.ReactNode]> = [
    ['Артикул продавца', <span key="a" className="font-mono">{card.article}</span>],
    ['Категория', preview?.category_name || <NoData key="c" why="категория площадки не определена" />],
    ['Бренд', brand ? String(brand) : <NoData key="b" />],
    ['Штрихкод', barcodes ? <span key="bc" className="font-mono">{barcodes}</span> : <NoData key="bc" why={card.marketplace === 'wb' ? 'баркод присвоит площадка' : undefined} />],
    ['Габариты упаковки', dimensionsText ?? <NoData key="d" />],
    ['Характеристики', preview
      ? filledText
      : <NoData key="ch" why="тело отправки не собрано" />],
  ]

  const overviewImage = editableImages[0]

  return (
    <>
      <CatalogCardModal
        isOpen
        ariaLabel={`Карточка PIM ${card.article}`}
        keys={cards.map((item) => String(item.id))}
        activeKey={String(card.id)}
        totalCount={totalCount}
        onNavigate={(key) => guard(() => onNavigate(Number(key)))}
        onClose={() => guard(onClose)}
        canGoBack={canGoBack}
        onBack={onBack ? () => guard(onBack) : undefined}
        suspendHotkeys={unsavedOpen || technicalOpen || exclusionDialogOpen}
        loading={loading}
        kind={{ label: `Черновик ${KIND_MARKETPLACE[card.marketplace]}`, icon: FileEdit }}
        primaryId={{ value: card.article, label: 'артикул', link: draftWindowLink(card.id) }}
        badges={(
          <>
            {stateBadge}
            {buildBadge}
            {excludedBadge}
          </>
        )}
        title={headerTitle}
        meta={metaItems}
        moreMenu={moreMenu}
        chain={<EntityChain items={chainItems} onBack={canGoBack ? () => guard(onBack ?? (() => undefined)) : undefined} />}
        banner={actionError ? (
          <div className="shrink-0 border-b border-red-200 bg-red-50 px-5 py-3 text-sm text-red-700 dark:border-red-950 dark:bg-red-950/40 dark:text-red-300">
            {actionError}
          </div>
        ) : undefined}
        nextStep={(
          <NextStepBar
            tone={stateTone}
            title={stateTitle}
            hint={stateHint}
            actions={nextStepActions}
            checks={loading ? undefined : checks}
            extra={errorPlate}
          />
        )}
        tabsHotkeys={{ keys: tabs.map((item) => item.key), onSelect: (key) => setTab(key as PimDraftTab) }}
        onSave={() => { if (hasUnsaved && canUpdate && !saving) void submitOverrides() }}
        tabs={(
          <UnderlineTabs<PimDraftTab>
            tabs={tabs.map((item) => ({
              key: item.key,
              label: item.label,
              icon: item.icon,
              count:
                item.key === 'characteristics'
                  ? contentCharacteristicDefinitions.length || payloadFilled || undefined
                  : item.key === 'media'
                    ? resolvedImages.length || undefined
                    : undefined,
              attention: tabAttention[item.key],
            }))}
            active={tab}
            onChange={setTab}
            ariaLabel="Разделы карточки черновика"
          />
        )}
        sidebar={(
          <CardSidebar
            images={resolvedImages}
            activeImage={Math.min(activeImage, Math.max(resolvedImages.length - 1, 0))}
            onActiveImage={setActiveImage}
            onOpenImage={(index) => setLightboxIndex(index)}
            // «+N» ведёт на «Медиа»: там весь набор целиком и правка порядка.
            onShowAll={() => setTab('media')}
            emptyImageText="В теле отправки нет визуалов"
            /*
              Файлы черновика лежат за гейтвеем и требуют токен: обычный <img>
              получил бы 401, поэтому колонке передаётся авторизованный загрузчик.
            */
            renderImage={(src, alt, className) => (
              <AuthorizedMediaImage src={src} alt={alt} thumbnailWidth={640} className={className} />
            )}
            stats={[
              hasPrice
                ? { label: 'Цена к отправке', wide: true, value: money(outboundPrice) }
                : { label: 'Цена к отправке', wide: true, value: NO_DATA, tone: 'dim', note: priceWhy },
              preview
                ? {
                    label: 'Характеристики',
                    value: definitionsKnown ? filledText : payloadFilled,
                    note: definitionsKnown ? 'заполнено в теле отправки' : 'в теле отправки',
                    tone: payloadFilled === 0 && !definitionsKnown ? 'bad' : undefined,
                  }
                : { label: 'Характеристики', value: NO_DATA, tone: 'dim', note: 'тело отправки не собрано' },
              preview
                ? {
                    label: 'Визуалы',
                    value: resolvedImages.length,
                    tone: resolvedImages.length ? undefined : 'bad',
                    note: resolvedImages.length ? 'в теле отправки' : 'в теле отправки нет',
                  }
                : { label: 'Визуалы', value: NO_DATA, tone: 'dim', note: 'тело отправки не собрано' },
              preview?.source?.card_revision
                ? { label: 'Ревизия карточки', value: `#${preview.source.card_revision}`, note: 'заморожена' }
                : { label: 'Ревизия карточки', value: NO_DATA, tone: 'dim' },
              preview?.source?.seo_revision_id
                ? {
                    label: 'SEO-ревизия',
                    value: `#${preview.source.seo_revision_id}`,
                    note: preview.source.seo_revision ? `версия ${preview.source.seo_revision}` : undefined,
                  }
                : { label: 'SEO-ревизия', value: NO_DATA, tone: 'dim' },
            ]}
            origins={[
              {
                label: 'Товар ПИМ',
                value: card.article,
                mono: true,
                href: `/products?article=${encodeURIComponent(card.article)}`,
              },
              ...(designCode
                ? [{
                    label: 'Макет',
                    value: `#${designCode}`,
                    mono: true,
                    href: `/designs?design=${encodeURIComponent(String(designCode))}`,
                  }]
                : noDesign ? [{ label: 'Макет', value: 'Без макета' }] : []),
              { label: 'Кабинет', value: sellerLabel },
            ]}
          />
        )}
        footer={(
          <>
            {footerState}
            <span className="ml-auto flex items-center gap-2">
              <Button
                size="sm"
                variant="secondary"
                disabled={saving || !hasUnsaved}
                title={!hasUnsaved ? 'Нет несохранённых правок' : undefined}
                onClick={cancelEdits}
              >
                Отменить
              </Button>
              <span title={saveDisabledReason}>
                <Button
                  size="sm"
                  loading={saving}
                  disabled={Boolean(saveDisabledReason)}
                  onClick={() => void submitOverrides()}
                >
                  Сохранить правки
                </Button>
              </span>
              {onPublish && publishable && (
                <span title={publishDisabledReason()}>
                  <Button
                    size="sm"
                    variant="secondary"
                    icon={<CloudUpload className="h-4 w-4" />}
                    disabled={Boolean(publishDisabledReason())}
                    onClick={doPublish}
                  >
                    Выгрузить…
                  </Button>
                </span>
              )}
            </span>
          </>
        )}
      >
        {tab === 'overview' && (
          <div className="space-y-5">
            {!preview?.exact && !previewError && (
              <div className="rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-950 dark:bg-amber-950/30 dark:text-amber-200">
                <AlertTriangle className="mr-2 inline h-4 w-4" /> Ниже не подставляются данные PIM: пока сборщик не выдаст payload, карточка считается неготовой.
              </div>
            )}

            <section>
              <SectionTitle>Как увидит покупатель</SectionTitle>
              <div className="flex gap-4 rounded-md border border-gray-200 p-4 dark:border-zinc-800">
                <div className="aspect-[3/4] w-32 shrink-0 overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-zinc-700">
                  {overviewImage ? (
                    <AuthorizedMediaImage
                      src={overviewImage}
                      alt="Главное фото"
                      thumbnailWidth={320}
                      className="h-full w-full object-contain"
                    />
                  ) : (
                    <div className="flex h-full items-center justify-center px-2 text-center text-xs text-red-600 dark:text-red-400">
                      Нет главного фото
                    </div>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-base font-semibold text-gray-900 dark:text-zinc-100">{headerTitle}</div>
                  <div className="mt-1 text-lg font-semibold tabular-nums text-gray-900 dark:text-zinc-100">
                    {hasPrice ? money(outboundPrice) : <NoData why={priceWhy} />}
                  </div>
                  <dl className="mt-3 grid grid-cols-1 gap-x-6 gap-y-1.5 text-sm sm:grid-cols-2">
                    {rows.map(([label, value]) => (
                      <div key={label} className="flex min-w-0 gap-2">
                        <dt className="shrink-0 text-xs text-gray-500 dark:text-zinc-400">{label}</dt>
                        <dd className="m-0 min-w-0 break-words text-gray-900 dark:text-zinc-100">{value}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              </div>
            </section>

            <section>
              <SectionTitle>Из чего собрано</SectionTitle>
              <dl className="rounded-md border border-gray-200 px-4 dark:border-zinc-800">
                <Kv label="SEO-ревизия">
                  {preview?.source?.seo_revision_id ? (
                    <span className="font-mono">
                      #{preview.source.seo_revision_id}
                      {preview.source.seo_revision ? ` · версия ${preview.source.seo_revision}` : ''}
                      {preview.source.seo_content_id ? ` · контент ${preview.source.seo_content_id}` : ''}
                    </span>
                  ) : <NoData why="текст ещё не заморожен" />}
                </Kv>
                <Kv label="Ревизия карточки">
                  {preview?.source?.card_revision ? (
                    <span>
                      <span className="font-mono">#{preview.source.card_revision}</span>
                      {preview.source.frozen_at ? ` · заморожена ${formatDraftDateTime(preview.source.frozen_at)}` : ''}
                    </span>
                  ) : <NoData why="ревизия не зафиксирована" />}
                </Kv>
                <Kv label="Источник характеристик">
                  {charSourcesText ?? <NoData why="источники не переданы сборщиком" />}
                </Kv>
                <Kv label="Источник визуалов">
                  {preview ? (
                    <span>
                      {mediaUrls.length} в теле отправки
                      {preview.overrides?.media_urls ? ' · порядок задан вручную' : ' · из воронки кабинета'}
                      {card.cabinet ? ` · ${card.cabinet}` : ''}
                    </span>
                  ) : <NoData why="тело отправки не собрано" />}
                </Kv>
                <Kv label="Цена">
                  {hasPrice ? (
                    <span>
                      {money(outboundPrice)}
                      {overriddenFields.has('price') ? ' · ручная правка' : ' · из категории товара'}
                    </span>
                  ) : <NoData why={priceWhy} />}
                </Kv>
              </dl>
            </section>

            <section>
              <SectionTitle>Ответ площадки</SectionTitle>
              <div className="rounded-md border border-gray-200 p-4 text-sm dark:border-zinc-800">
                {refused && refusal ? (
                  <div className="space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant="red">{refusal.label}</Badge>
                      {refusal.code && <span className="font-mono text-xs text-gray-500 dark:text-zinc-400">{refusal.code}</span>}
                    </div>
                    {refusal.explanation && (
                      <p className="m-0 whitespace-pre-line text-gray-700 dark:text-zinc-300">{refusal.explanation}</p>
                    )}
                    {refusal.raw && (
                      <pre className="m-0 max-h-40 overflow-auto whitespace-pre-wrap rounded-md bg-gray-50 p-2 text-xs text-gray-700 dark:bg-zinc-950 dark:text-zinc-300">{refusal.raw}</pre>
                    )}
                  </div>
                ) : published ? (
                  <p className="m-0 text-gray-700 dark:text-zinc-300">
                    Площадка приняла карточку, внешний ID <span className="font-mono">{card.external_id}</span>.
                  </p>
                ) : unanswered ? (
                  <p className="m-0 text-gray-700 dark:text-zinc-300">
                    Запрос ушёл, ответа площадки не было. Последняя запись по карточке: {sentAt}.
                    {status.hint ? ` ${status.hint}` : ''}
                  </p>
                ) : submitState === 'in_flight' ? (
                  <p className="m-0 text-gray-700 dark:text-zinc-300">Запрос к площадке выполняется прямо сейчас.</p>
                ) : (
                  <p className="m-0 text-gray-500 dark:text-zinc-400">Ещё не отправлялся: площадка по этой карточке не отвечала.</p>
                )}
              </div>
            </section>
          </div>
        )}

        {tab === 'text' && (
          <div className="space-y-5">
            {!preview?.exact && !previewError && (
              <div className="rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-950 dark:bg-amber-950/30 dark:text-amber-200">
                <AlertTriangle className="mr-2 inline h-4 w-4" /> Ниже не подставляются данные PIM: пока сборщик не выдаст payload, карточка считается неготовой.
              </div>
            )}
            {preview?.exact && (
              <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800 dark:border-emerald-950 dark:bg-emerald-950/30 dark:text-emerald-200">
                <CheckCircle2 className="mr-2 inline h-4 w-4" /> Это фактические поля outbound payload, без подмены сводными данными списка.
              </div>
            )}
            {platformPayload && (
              <>
                <section>
                  <SectionTitle>Текст карточки для {marketplaceName}</SectionTitle>
                  <dl className="rounded-md border border-gray-200 px-4 dark:border-zinc-800">
                    <Kv label="Наименование" apiKey={card.marketplace === 'wb' ? 'title' : 'name'}>
                      <input
                        value={form.title}
                        onChange={(event) => setForm({ ...form, title: event.target.value })}
                        className={editField(overriddenFields.has('title'))}
                      />
                      {profile.titleMaxLength && (
                        <span className={`mt-1 block text-[11px] tabular-nums ${form.title.length > profile.titleMaxLength ? 'text-red-600 dark:text-red-400' : 'text-gray-400 dark:text-zinc-500'}`}>
                          {form.title.length} / {profile.titleMaxLength}
                        </span>
                      )}
                      {overriddenFields.has('title') && <ManualMark />}
                    </Kv>
                    <Kv label="Описание" apiKey="description">
                      <textarea
                        rows={8}
                        value={form.description}
                        onChange={(event) => setForm({ ...form, description: event.target.value })}
                        className={`${editField(overriddenFields.has('description'))} resize-y`}
                      />
                      {overriddenFields.has('description') && <ManualMark />}
                    </Kv>
                  </dl>
                </section>

                <section>
                  <SectionTitle>Прочие поля карточки</SectionTitle>
                  <dl className="rounded-md border border-gray-200 px-4 dark:border-zinc-800">
                    <Kv
                      label="Артикул продавца"
                      apiKey={card.marketplace === 'wb' ? 'vendorCode' : card.marketplace === 'ym' ? 'offerId' : 'offer_id'}
                    >
                      <span className="font-mono">
                        {card.marketplace === 'wb'
                          ? wbVariant?.vendorCode
                          : card.marketplace === 'ym'
                            ? ymOffer?.offerId
                            : ozonItem?.offer_id}
                      </span>
                    </Kv>
                    {card.marketplace === 'wb' ? (
                      <>
                        {/* Предмет человек знает по названию, номер — для сверки со справочником. */}
                        <Kv
                          label="Предмет"
                          apiKey={
                            wbCardUpload?.subjectID
                              ? `subjectID ${wbCardUpload.subjectID}`
                              : 'subjectID'
                          }
                        >
                          {preview?.category_name || (
                            <span className="font-mono">{textValue(wbCardUpload?.subjectID)}</span>
                          )}
                        </Kv>
                        <Kv label="Бренд" apiKey="brand">{textValue(wbVariant?.brand)}</Kv>
                        {/*
                          sizes у безразмерного товара — это цена и баркод, а не
                          размерная сетка. Печатать массив скобками значило просить
                          человека разбирать JSON ради двух чисел.
                        */}
                        <Kv label="Цена и баркоды" apiKey="sizes">
                          <span className="flex flex-wrap items-center gap-2">
                            <input
                              type="number"
                              min="1"
                              value={form.price}
                              onChange={(event) => setForm({ ...form, price: event.target.value })}
                              className={`${editField(overriddenFields.has('price'))} w-32`}
                            />
                            <span className="text-gray-500 dark:text-zinc-400">₽</span>
                            <span className="text-gray-500 dark:text-zinc-400">
                              {wbVariant?.sizes?.[0]?.skus?.length
                                ? `баркоды: ${wbVariant.sizes[0].skus.join(', ')}`
                                : 'баркод присвоит площадка'}
                            </span>
                          </span>
                          {overriddenFields.has('price') && <ManualMark />}
                        </Kv>
                        <Kv label="Габариты упаковки и вес брутто" apiKey="dimensions">
                          <DimensionFields
                            form={form}
                            setForm={setForm}
                            overridden={overriddenFields.has('dimensions')}
                          />
                        </Kv>
                      </>
                    ) : card.marketplace === 'ozon' ? (
                      <>
                        <Kv
                          label="Категория площадки"
                          apiKey={
                            ozonItem?.description_category_id
                              ? `description_category_id ${ozonItem.description_category_id}`
                              : 'description_category_id'
                          }
                        >
                          {preview?.category_name || (
                            <span className="font-mono">
                              {textValue(ozonItem?.description_category_id)}
                            </span>
                          )}
                        </Kv>
                        <Kv label="Тип товара" apiKey="type_id">
                          <span className="font-mono">{textValue(ozonItem?.type_id)}</span>
                        </Kv>
                        <Kv label="Цена" apiKey="price / old_price">
                          <span className="flex flex-wrap items-center gap-2">
                            <input
                              type="number"
                              min="1"
                              value={form.price}
                              onChange={(event) => setForm({ ...form, price: event.target.value })}
                              className={`${editField(overriddenFields.has('price'))} w-32`}
                            />
                            <span className="text-gray-500 dark:text-zinc-400">₽</span>
                            {Number(ozonItem?.old_price) > 0 && (
                              <span className="text-gray-400 line-through dark:text-zinc-500">
                                {money(ozonItem?.old_price)}
                              </span>
                            )}
                          </span>
                          {overriddenFields.has('price') && <ManualMark />}
                        </Kv>
                        <Kv label="НДС" apiKey="vat">
                          {ozonItem?.vat != null ? `${ozonItem.vat}` : '—'}
                        </Kv>
                        <Kv label="Бренд" apiKey="vendor">{textValue(ozonItem?.vendor)}</Kv>
                        <Kv label="Штрихкод" apiKey="barcode">{textValue(ozonItem?.barcode)}</Kv>
                        <Kv label="Габариты упаковки и вес брутто" apiKey="width / height / depth / weight">
                          <DimensionFields
                            form={form}
                            setForm={setForm}
                            overridden={overriddenFields.has('dimensions')}
                          />
                        </Kv>
                      </>
                    ) : card.marketplace === 'ym' ? (
                      <>
                        <Kv
                          label="Категория площадки"
                          apiKey={
                            ymOffer?.marketCategoryId
                              ? `marketCategoryId ${ymOffer.marketCategoryId}`
                              : 'marketCategoryId'
                          }
                        >
                          {preview?.category_name || (
                            <span className="font-mono">{textValue(ymOffer?.marketCategoryId)}</span>
                          )}
                        </Kv>
                        <Kv label="Бренд" apiKey="vendor">{textValue(ymOffer?.vendor)}</Kv>
                        <Kv label="Штрихкоды" apiKey="barcodes">
                          {Array.isArray(ymOffer?.barcodes) && ymOffer.barcodes.length > 0
                            ? <span className="font-mono">{ymOffer.barcodes.join(', ')}</span>
                            : '—'}
                        </Kv>
                        <Kv label="Габариты упаковки и вес брутто" apiKey="weightDimensions">
                          <DimensionFields
                            form={form}
                            setForm={setForm}
                            overridden={overriddenFields.has('dimensions')}
                          />
                        </Kv>
                        <p className="py-2.5 text-xs text-gray-500 dark:text-zinc-400">
                          Цена в оффер не входит — уходит отдельным шагом после создания карточки
                          (offer-prices/updates), поэтому здесь не редактируется.
                        </p>
                      </>
                    ) : (
                      assertNever(card.marketplace)
                    )}
                  </dl>
                </section>
              </>
            )}
          </div>
        )}

        {tab === 'characteristics' && (
          <section>
            <SectionTitle>Характеристики карточки</SectionTitle>
            <p className="mb-3 text-xs text-gray-500 dark:text-zinc-400">
              Набор выбранной категории площадки: категорийные значения можно переопределить
              для товара, товарные — заполнить здесь впервые. Пустые поля в payload не
              отправляются. Видео, обложка и rich-контент — на вкладке «Медиа».
            </p>
            {characteristicsLoading ? (
              <div className="flex items-center gap-2 rounded-md border border-gray-200 px-4 py-8 text-sm text-gray-500 dark:border-zinc-800 dark:text-zinc-400">
                <Loader2 className="h-4 w-4 animate-spin" /> Загрузка состава характеристик…
              </div>
            ) : characteristicsError ? (
              <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-950 dark:bg-red-950/30 dark:text-red-300">
                {characteristicsError}
              </div>
            ) : contentCharacteristicDefinitions.length ? (
              <dl className="rounded-md border border-gray-200 px-4 dark:border-zinc-800">
                {contentCharacteristicDefinitions.map(characteristicField)}
              </dl>
            ) : (
              <div className="rounded-md border border-dashed border-gray-300 px-4 py-12 text-center text-sm text-gray-400 dark:border-zinc-700">Для выбранной категории нет применимых характеристик</div>
            )}
          </section>
        )}

        {tab === 'media' && (
          <section className="space-y-5">
            <MediaRibbon
              urls={mediaUrls}
              onChange={setMediaUrls}
              article={card.article}
              cabinet={card.cabinet}
              maxCount={card.marketplace === 'wb' ? 30 : undefined}
              aspect={card.marketplace === 'wb' ? '3:4' : '1:1'}
              baselineUrls={initialMediaUrls}
              scopes={RIBBON_SCOPES}
              scope="card"
              affectedCards={1}
              onOpen={(index) => setLightboxIndex(index)}
              resolveSrc={(url) => getMediaUrl(url, Number(card.design_code) || undefined)}
              renderImage={(src, alt, className) => (
                <AuthorizedMediaImage src={src} alt={alt} thumbnailWidth={640} className={className} />
              )}
              hint={(
                <div className="rounded-md border border-indigo-100 bg-indigo-50 px-3 py-2 text-xs text-indigo-800 dark:border-indigo-950 dark:bg-indigo-950/30 dark:text-indigo-200">
                  Точный порядок из тела отправки. У ВБ это второй шаг media/save после получения nmID,
                  у OZON — массив images в product/import.
                </div>
              )}
            />
            {mediaCharacteristicDefinitions.length > 0 && (
              <section>
                <SectionTitle>Видео и rich-контент</SectionTitle>
                <dl className="rounded-md border border-gray-200 px-4 dark:border-zinc-800">
                  {mediaCharacteristicDefinitions.map(characteristicField)}
                </dl>
              </section>
            )}
          </section>
        )}

        {tab === 'merge' && (
          <section>
            <SectionTitle>Склейка</SectionTitle>
            {card.merge_label || card.merge_key || card.bundle_id || card.imt_id ? (
              <dl className="rounded-md border border-gray-200 px-4 dark:border-zinc-800">
                <Kv label="Склейка">{card.merge_label || <NoData />}</Kv>
                <Kv label="Ключ склейки" apiKey="merge_key">
                  {card.merge_key ? <span className="font-mono">{card.merge_key}</span> : <NoData why="у исторических карточек ключа нет" />}
                </Kv>
                <Kv label="Склейка в мастере" apiKey="bundle_id">
                  {card.bundle_id ? <span className="font-mono">#{card.bundle_id}</span> : <NoData why="склейка как сущность не создана" />}
                </Kv>
                {card.marketplace === 'wb' && (
                  <Kv label="Карточка ВБ (imtID)" apiKey="imt_id">
                    {card.imt_id ? <span className="font-mono">{card.imt_id}</span> : <NoData why="номенклатура ещё не вошла в склеенную карточку" />}
                  </Kv>
                )}
              </dl>
            ) : (
              <div className="rounded-md border border-dashed border-gray-300 px-4 py-12 text-center text-sm text-gray-500 dark:border-zinc-700 dark:text-zinc-400">
                Карточка не входит в склейку. Склейка собирается из нескольких черновиков одной
                тематики на странице площадки («Создать склейку»); имя склейки уезжает в «Название
                модели» при первой публикации.
              </div>
            )}
          </section>
        )}

        {tab === 'history' && (
          <section>
            <SectionTitle>История карточки</SectionTitle>
            <dl className="rounded-md border border-gray-200 px-4 dark:border-zinc-800">
              <Kv label="Создана">{formatDraftDateTime(card.created_at)}</Kv>
              <Kv label="Изменена">{formatDraftDateTime(card.updated_at)}</Kv>
              <Kv label="Опубликована">{formatDraftDateTime(card.published_at)}</Kv>
              <Kv label="Продажи включены">{formatDraftDateTime(card.sales_enabled_at)}</Kv>
              <Kv label="Исключена из выгрузки">
                {card.publish_excluded_at
                  ? `${formatDraftDateTime(card.publish_excluded_at)}${card.publish_excluded_reason ? ` · ${card.publish_excluded_reason}` : ''}`
                  : 'нет'}
              </Kv>
              <Kv label="Ревизия карточки">
                {preview?.source?.card_revision ? (
                  <span>
                    <span className="font-mono">#{preview.source.card_revision}</span>
                    {` · зафиксирована ${formatDraftDateTime(preview.source.frozen_at)}`}
                  </span>
                ) : <NoData why="ревизия не зафиксирована" />}
              </Kv>
              <Kv label="SEO-ревизия">
                {preview?.source?.seo_revision_id ? (
                  <span className="font-mono">
                    #{preview.source.seo_revision_id}
                    {preview.source.seo_revision ? ` · версия ${preview.source.seo_revision}` : ''}
                  </span>
                ) : <NoData why="текст ещё не заморожен" />}
              </Kv>
            </dl>
            <p className="mt-3 text-xs text-gray-500 dark:text-zinc-400">
              Полная история правок и отправок по карточке появится вместе с журналом изменений.
            </p>
          </section>
        )}
      </CatalogCardModal>

      <UnsavedChangesDialog
        isOpen={unsavedOpen}
        saving={saving}
        dirtyLabels={dirtyLabels}
        onCancel={() => {
          setUnsavedOpen(false)
          pendingAction.current = null
        }}
        onDiscard={() => {
          setUnsavedOpen(false)
          cancelEdits()
          const action = pendingAction.current
          pendingAction.current = null
          if (action) action()
        }}
        onSaveAndContinue={() => {
          void submitOverrides().then((saved) => {
            if (!saved) return
            setUnsavedOpen(false)
            const action = pendingAction.current
            pendingAction.current = null
            if (action) action()
          })
        }}
      />

      {/* «Не выгружать» с причиной: запрет постоянный, поэтому спрашиваем зачем. */}
      <Modal
        isOpen={exclusionDialogOpen}
        onClose={() => { if (!exclusionBusy) setExclusionDialogOpen(false) }}
        title="Не выгружать карточку?"
        size="md"
        layer="nested"
        footer={(
          <>
            <Button variant="secondary" disabled={exclusionBusy} onClick={() => setExclusionDialogOpen(false)}>
              Отмена
            </Button>
            <Button
              loading={exclusionBusy}
              onClick={() => {
                setExclusionBusy(true)
                void Promise.resolve(onSetExclusion?.(card, true, exclusionReason.trim() || null))
                  .finally(() => {
                    setExclusionBusy(false)
                    setExclusionDialogOpen(false)
                  })
              }}
            >
              Не выгружать
            </Button>
          </>
        )}
      >
        <p className="text-sm text-gray-700 dark:text-zinc-300">
          Карточка «{card.article}» будет исключена из выгрузки: ни кнопка, ни массовая
          выгрузка её не отправят, пока вы не вернёте её в «Ещё».
        </p>
        <label className="mt-3 block text-xs text-gray-500 dark:text-zinc-400">
          Причина (необязательно)
          <textarea
            rows={3}
            value={exclusionReason}
            onChange={(event) => setExclusionReason(event.target.value)}
            className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 outline-none focus:ring-2 focus:ring-indigo-500 dark:border-zinc-700 dark:bg-zinc-950 dark:text-zinc-100"
          />
        </label>
      </Modal>

      {/* Бывшая вкладка «Диагностика»: ничего не потеряно, просто убрано с основного пути. */}
      <Modal
        isOpen={technicalOpen}
        onClose={() => setTechnicalOpen(false)}
        title="Технические данные"
        subtitle={`Черновик ${card.article} · ${marketplaceName}`}
        size="xl"
        layer="nested"
      >
        <div className="space-y-5">
          <section>
            <SectionTitle>Замороженный источник</SectionTitle>
            <dl className="rounded-md border border-gray-200 px-4 dark:border-zinc-800">
              <Kv label="Кабинет">{sellerLabel}</Kv>
              <Kv label="Карточка в реестре публикаций" apiKey="marketplace_cards.id">
                <span className="font-mono">#{card.id}</span>
              </Kv>
              <Kv label="Ревизия карточки">
                <span className="font-mono">
                  {preview?.source?.card_revision ? `#${preview.source.card_revision}` : '—'}
                </span>
              </Kv>
              <Kv label="SEO-контент и ревизия" apiKey="seo_content_id / seo_revision_id">
                <span className="font-mono">
                  {preview?.source
                    ? `${preview.source.seo_content_id ?? '—'} / ${preview.source.seo_revision_id ?? '—'} (версия ${preview.source.seo_revision ?? '—'})`
                    : '—'}
                </span>
              </Kv>
              <Kv label="Прогон публикации" apiKey="run_id">
                {preview?.source?.run_id ? (
                  <span className="font-mono">{preview.source.run_id}</span>
                ) : (
                  // Раньше здесь стояло «legacy latest revision» — техническая
                  // пометка кода, из которой нельзя понять, что произошло.
                  <span className="text-gray-500 dark:text-zinc-400">
                    Прогон не зафиксирован — взята последняя ревизия
                  </span>
                )}
              </Kv>
              <Kv label="Зафиксировано">{formatDraftDateTime(preview?.source?.frozen_at)}</Kv>
              <Kv label="Режим">{publicationModeLabel(card.publication_mode)}</Kv>
              <Kv label="Статус">{status.label}</Kv>
              <Kv label="Опубликовано">{formatDraftDateTime(card.published_at)}</Kv>
              <Kv label="Продажи включены">{formatDraftDateTime(card.sales_enabled_at)}</Kv>
              <Kv label="Состояние сборки">{preview?.exact ? 'Готов к отправке' : 'Не готов'}</Kv>
            </dl>
          </section>
          <section>
            <SectionTitle>Сырой payload</SectionTitle>
            <pre className="max-h-[32rem] overflow-auto rounded-md border border-gray-200 bg-gray-950 p-4 text-xs leading-5 text-emerald-200 dark:border-zinc-800">
              {platformPayload ? JSON.stringify(platformPayload, null, 2) : 'Payload не сформирован'}
            </pre>
          </section>
        </div>
      </Modal>

      {/* Лайтбокс поверх окна: файлы черновика защищены, поэтому свой. */}
      <AuthorizedLightbox
        isOpen={lightboxIndex != null}
        sources={editableImages}
        index={lightboxIndex ?? 0}
        onIndexChange={setLightboxIndex}
        onClose={() => setLightboxIndex(null)}
        label={(position) => `Визуал ${position + 1} из ${editableImages.length}`}
      />
    </>
  )
}

export default PimDraftCardModal
